import React, { useCallback, useEffect, useState } from 'react';
import { Box, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';

import type { Career } from '@/types/Content/Career';

import { Container } from '@/components/layout';
import useUpdateEffect from '@/hooks/use-update-effect';
import contactUsService from '@/services/contactUsService';

import ShowMoreButton from '../Village/Tabs/HomeTab/components/ShowMoreButton';
import CareersSlider from './CareersSlider';
import CareerCard from './CareerCard';

const VACANCIES = [
  {
    id: '0',
    title: 'Account Manager',
    description: 'Client Success\nRemote, United States',
  },
  {
    id: '1',
    title: 'Health and Wellbeing\nSpecialist & Coach',
    description: 'Professional Services\nRemote, United States',
  },
  {
    id: '2',
    title: 'PT Disease Management\nNurse -RN',
    description: 'Care Management\nRemote, United States',
  },
];

function Careers() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [vacancies, setVacancies] = useState<Career[]>();

  const [vacanciesMobile, setVacanciesMobile] = useState<Career[]>();

  const canShowMore =
    VACANCIES.length > 3 && vacanciesMobile.length !== VACANCIES.length;

  useEffect(() => {
    contactUsService
      .getCareers()
      .then((data) => {
        setVacancies(data?.items);
        setVacanciesMobile(data?.items.slice(0, 3));
      })
      .catch((error) => {});
  }, []);

  useUpdateEffect(() => {
    if (isMobile) {
      setVacanciesMobile(vacancies?.slice(0, 3));
    }
  }, [isMobile, vacancies]);

  const handleMoreClick = useCallback(() => {
    setVacanciesMobile(vacancies);
  }, [vacancies]);

  return (
    <Stack
      component="section"
      sx={{
        paddingTop: { xs: theme.spacing(6), md: theme.spacing(12.5) },
        paddingBottom: theme.spacing(15),
        backgroundColor: '#E5EDF8',
      }}
    >
      <Container>
        <Stack
          sx={{
            gap: theme.spacing(7.5),
          }}
        >
          <Stack
            sx={{
              gap: { xs: 4, md: 6 },
              flexDirection: { xs: 'column', md: 'row' },
            }}
          >
            <Typography
              fontSize="3.125rem !important"
              lineHeight="1.18 !important"
              variant="h2"
              color={theme.palette.common.white}
              sx={{
                color: '#34353E',
                fontSize: '2.375rem !important',
                lineHeight: '1.2 !important',
              }}
            >
              Careers
            </Typography>

            <Box
              sx={{
                width: '2px',
                height: 'auto',
                display: { xs: 'none', md: 'block' },
                borderRadius: '3px',
                backgroundColor: '#82848E',
              }}
            />

            <Typography
              variant="body2"
              sx={{
                color: '#52545F',
                fontSize: '1.125rem !important',
                lineHeight: '1.46 !important',
              }}
            >
              We’re empowering people to live happier,{' '}
              <Box
                component="br"
                sx={{
                  display: { xs: 'none', sm: 'block' },
                }}
              />
              healthier lives. Every day.
            </Typography>
          </Stack>

          {!isMobile && (
            <Stack sx={{ display: { xs: 'none', md: 'flex' } }}>
              <CareersSlider arrowColor="black">
                {vacancies?.map(
                  ({ title, category, location, apply_link: link }) => (
                    <CareerCard
                      key={`${title}-${category}-${location}-${link}`}
                      link={link}
                      title={title}
                      category={category}
                      location={location}
                    />
                  ),
                )}
              </CareersSlider>
            </Stack>
          )}

          {isMobile && (
            <Stack
              sx={{
                display: { xs: 'flex', md: 'none' },
                flexDirection: 'column',
                gap: 6,
                overflow: 'hidden',
              }}
            >
              {vacanciesMobile.map(
                ({ title, category, location, apply_link: link }) => (
                  <CareerCard
                    key={`${title}-${category}-${location}-${link}`}
                    link={link}
                    title={title}
                    category={category}
                    location={location}
                  />
                ),
              )}

              <ShowMoreButton
                loading={false}
                nextPageExists={canShowMore}
                loadNextPage={handleMoreClick}
                sx={{
                  display: { xs: 'inline-block', md: 'none' },
                  color: '#52545F',
                  fontSize: theme.spacing(4),
                  lineHeight: '1.46',
                  backgroundColor: '#F3F4F8',
                  minHeight: theme.spacing(12.25),
                  textDecorationThickness: '1px !important',
                }}
              >
                Show More
              </ShowMoreButton>
            </Stack>
          )}
        </Stack>
      </Container>
    </Stack>
  );
}

export default Careers;
